import { useEffect, useMemo, useRef, useState } from "react";
import ProjectReviewApi from "../../../../api/ProjectReviewApi";
import AiQuotationPreview from "./AiQuotationPreview";
import AiProgressCarousel from "./AiProgressCarousel";
import "./AiProjectProcessing.css";

const DEFAULT_STEPS = [
  { key: "init", label: "Creating project" },
  { key: "pre", label: "Generating setup PDF | SOW" },
  { key: "text", label: "Generating AI description" },
  { key: "images", label: "Generating AI renders" },
  { key: "final", label: "Finalizing project | Sending email" }
];

const REVISION_STEPS = [
  { key: "images", label: "Applying AI Revision" },
  { key: "final", label: "Updating Project" }
];

export default function AiProjectProcessing({ payload }) {

  // console.count("AiProjectProcessing mounted");

  const [activeKey, setActiveKey] = useState("init");
  const [error, setError] = useState("");
  const [runKey, setRunKey] = useState(0);

  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionPrompt, setRevisionPrompt] = useState("");
  const [payloadOverride, setPayloadOverride] = useState(null);


  const effectivePayload = payloadOverride || payload;

  const [showRenderModal, setShowRenderModal] = useState(false);
  const [renderReady, setRenderReady] = useState(false);

  const isRevision =
    Boolean(effectivePayload?.projectId) &&
    Boolean(effectivePayload?.aiRevisionPrompt?.trim());

  const STEPS = isRevision
    ? REVISION_STEPS
    : DEFAULT_STEPS;

  const [ctx, setCtx] = useState({
    projectId: null,
    prePdfUrl: null,
    postPdfUrl: null,
    aiTextJson: null,
    aiTextHtml: null,
    aiImages: null,
    project: null
  });

  const activeLabel = useMemo(
    () => STEPS.find(s => s.key === activeKey)?.label || "Processing…",
    [activeKey]
  );


  useEffect(() => {

    // console.count("useEffect started");

    if (!effectivePayload) return;

    let cancelled = false;

    async function run() {
      // console.count("run()");
      // console.log("Payload object:", effectivePayload);
      // console.log("Same object:", effectivePayload === payload);
      try {
        setError("");

        let aiTextResult = null;

        const revisionOnly = isRevision;

        let projectId = ctx.projectId;

        if (isRevision) {

          projectId = effectivePayload.projectId;

          setCtx(p => ({
            ...p,
            projectId
          }));

          /* =====================================================
             LOAD CURRENT PROJECT FIRST
          ===================================================== */

          const currentProject =
            await ProjectReviewApi.getProject(
              projectId,
              effectivePayload.contact.email
            );

          if (cancelled) return;

          setCtx(p => ({
            ...p,
            project: currentProject,
            aiImages: currentProject?.aiImages || [],
            aiTextJson: currentProject?.aiText?.json || null,
            aiTextHtml: currentProject?.aiText?.html || null,
            prePdfUrl:
              currentProject?.prePdf?.url ||
              currentProject?.prePdfUrl ||
              null,
            postPdfUrl:
              currentProject?.pdf?.url ||
              currentProject?.pdfUrl ||
              null
          }));

          /* =====================================================
             START AI REVISION
          ===================================================== */

          setActiveKey("images");

          await ProjectReviewApi.generateAiImages({
            projectId,
            payload: effectivePayload
          });

          await ProjectReviewApi.waitForAiImages({
            projectId,
            expectedImages: effectivePayload.images.length,
            email: effectivePayload.contact.email
          });

          /* =====================================================
             FINALIZE
          ===================================================== */

          setActiveKey("final");

          await ProjectReviewApi.finalizeProject({
            projectId,
            payload: effectivePayload
          });

          const refreshedProject =
            await ProjectReviewApi.getProject(
              projectId,
              effectivePayload.contact.email
            );

          if (cancelled) return;

          setCtx(p => ({
            ...p,
            project: refreshedProject,
            aiImages: refreshedProject?.aiImages || [],
            aiTextJson:
              refreshedProject?.aiText?.json ||
              p.aiTextJson,
            aiTextHtml:
              refreshedProject?.aiText?.html ||
              p.aiTextHtml,
            prePdfUrl:
              refreshedProject?.prePdf?.url ||
              refreshedProject?.prePdfUrl ||
              p.prePdfUrl,
            postPdfUrl:
              refreshedProject?.pdf?.url ||
              refreshedProject?.pdfUrl ||
              p.postPdfUrl
          }));

          setShowRenderModal(true);
          setRenderReady(false);

          setTimeout(() => {
            setRenderReady(true);
          }, 5000);

          return;

        }

        if (!revisionOnly) {
          // INIT + PRE PDF + AI TEXT
          /* ---------- INIT ---------- */
          setActiveKey("init");
          // console.count("AI PROCESSING useEffect");
          // console.log({
          //   isRevision,
          //   runKey,
          //   payloadProjectId: effectivePayload?.projectId,
          //   revisionPrompt: effectivePayload?.aiRevisionPrompt
          // });
          // console.count("initProject called");
          // console.trace("initProject stack");
          const init = await ProjectReviewApi.initProject(effectivePayload);
          if (cancelled) return;

          projectId = init?.projectId;
          if (!projectId) throw new Error("Init failed");

          setCtx(p => ({ ...p, projectId }));

          /* ---------- PRE PDF ---------- */
          try {
            setActiveKey("pre");
            const pre = await ProjectReviewApi.generatePrePdf({
              projectId,
              payload: effectivePayload
            });
            if (cancelled) return;

            setCtx(p => ({
              ...p,
              prePdfUrl: pre?.prePdfUrl || p.prePdfUrl
            }));
          } catch (err) {
            console.warn("Pre-PDF failed, continuing without it", err);

            setCtx(p => ({
              ...p,
              prePdfUrl: null
            }));
          }


          /* ---------- AI TEXT ---------- */
          setActiveKey("text");
          const text = await ProjectReviewApi.generateAiText({
            projectId,
            payload: effectivePayload
          });
          if (cancelled) return;

          aiTextResult = {
            json: text?.aiTextJson || text?.aiText?.json || null,
            html: text?.aiTextHtml || text?.aiText?.html || null
          };

          setCtx(p => ({
            ...p,
            aiTextJson: aiTextResult.json,
            aiTextHtml: aiTextResult.html
          }));


        }

        /* ---------- AI IMAGES ---------- */
        setActiveKey("images");

        await ProjectReviewApi.generateAiImages({
          projectId,
          payload: effectivePayload
        });

        if (cancelled) return;

        /* ---------- WAIT FOR AI (ANTI-TIMEOUT) ---------- */
        await ProjectReviewApi.waitForAiImages({
          projectId,
          expectedImages: effectivePayload.images?.length || 1,
          email: effectivePayload.contact.email,
          maxRetries: 32
        });

        if (cancelled) return;

        /* ---------- FINAL ---------- */
        setActiveKey("final");

        const final = await ProjectReviewApi.finalizeProject({
          projectId,
          payload: effectivePayload,
          // aiImages: aiImagesResult,
          // aiText: aiTextResult
        });

        const refreshedProject = await ProjectReviewApi.getProject(
          projectId,
          effectivePayload.contact.email
        );
        // console.log("REFRESHED PROJECT:", refreshedProject);

        // console.log("FINAL PROJECT");
        // console.dir(refreshedProject, { depth: null });

        if (cancelled) return;

        setCtx(p => ({
          ...p,
          project: refreshedProject || null,
          aiImages: refreshedProject?.aiImages || [],
          postPdfUrl:
            refreshedProject?.pdf?.url ||
            refreshedProject?.pdfUrl ||
            final?.postPdfUrl ||
            p.postPdfUrl
        }));

        setShowRenderModal(true);
        setRenderReady(false);

        setTimeout(() => {
          setRenderReady(true);
        }, 5000);

        return;


      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Process failed");
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [effectivePayload, runKey]);

  function handleResend() {
    setError("");
    setCtx({
      projectId: null,
      prePdfUrl: null,
      postPdfUrl: null,
      aiTextJson: null,
      aiTextHtml: null,
      aiImages: null,
      project: null
    });
    setActiveKey("init");
    setRunKey(k => k + 1);
  }


  async function handleApplyRevision() {
    try {
      const clean = revisionPrompt.trim();
      if (!clean) return;
      setRevisionOpen(false);
      console.log("🟢 Starting AI revision from previous AI render...");

      const revisedPayload = {
        ...effectivePayload,
        projectId: effectivePayload?.projectId || ctx.projectId,
        aiRevisionPrompt: clean,
        revisionRequestedAt: new Date().toISOString()
      };
      setRevisionPrompt("");
      setError("");
      setCtx(p => ({
        ...p,
        postPdfUrl: null,
        aiImages: null,
        project: null
      }));
      setPayloadOverride(revisedPayload);
      setActiveKey("images");
      // setRunKey(k => k + 1);
      console.log("✅ Restarting workflow with original image...");
    } catch (err) {
      console.error("❌ Revision failed", err);
      setError(err.message || "Revision failed");
      setRevisionOpen(true);
    }
  }

  const done = !error && activeKey === "final" && !!ctx.postPdfUrl;
  const canResend = !!error;

  const aiPreviewImage =
    ctx.project?.aiImages?.[0]?.url ||
    ctx.project?.previewImage ||
    ctx.aiImages?.[0]?.url ||
    null;


  return (

    <div className="ai-processing">

      <div className="ai-processing-shell">

        <div className="ai-progress-outside">
          <AiProgressCarousel
            activeKey={activeKey}
            error={!!error}
            done={done}
          />
        </div>

        <div className="ai-card">

          {canResend && (
            <button
              onClick={handleResend}
              className="ai-resend-btn"
            >
              Resend Project
            </button>
          )}

          <div className="ai-top">
            <div className={`ai-loader ${done ? "done" : ""}`} />
            <div className="ai-title">
              <h2>
                {error ? "Something went wrong" : done ? "Completed" : activeLabel}
              </h2>
              <p className="ai-sub">
                {error
                  ? "Please try again. If this persists, contact support."
                  : done
                    ? "Your project was generated successfully."
                    : "This can take a few minutes depending on image generation."}
              </p>
            </div>
          </div>

          <div className="ai-steps">
            {STEPS.map((s, i) => {
              const current = STEPS.findIndex(x => x.key === activeKey);
              const isActive = s.key === activeKey && !done && !error;
              const isDone = !error && i < current;

              return (
                <div
                  key={s.key}
                  className={`ai-step ${isActive ? "active" : ""} ${isDone ? "done" : ""}`}
                >
                  <span className="dot" />
                  <span className="label">{s.label}</span>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="ai-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {!!ctx.projectId && (
            <div className="ai-meta">
              <div className="ai-meta-row">
                <span className="k">Project ID</span>
                <span className="v">{ctx.projectId}</span>
              </div>
            </div>
          )}

          {(ctx.prePdfUrl || ctx.postPdfUrl) && (
            <div className="ai-links">
              {ctx.postPdfUrl && (
                <a className="ai-link" href={ctx.postPdfUrl} target="_blank" rel="noreferrer">
                  📄 Proposal
                </a>
              )}
              {ctx.prePdfUrl && (
                <a className="ai-link ghost" href={ctx.prePdfUrl} target="_blank" rel="noreferrer">
                  📋 Scope of Work (SOW)
                </a>
              )}
            </div>
          )}

          {done && (
            <button
              className="ai-revision-btn"
              onClick={() => setRevisionOpen(true)}
            >
              Request Visual Revision <strong style={{ fontSize: "19px" }}>✨</strong>
            </button>
          )}

          {aiPreviewImage &&
            renderReady &&
            (
              !isRevision ||
              done ||
              activeKey === "final"
            ) && (
              <div className="ai-render-preview">
                <div className="ai-render-preview-title">
                  Generated visual proposal
                </div>

                <img
                  src={aiPreviewImage}
                  alt="Generated AI render"
                />
              </div>
            )}

          <AiQuotationPreview aiText={ctx.aiTextJson} />

        </div>
        {/* <button
          onClick={handleResend}
          className="ai-resend-btn"
          style={{"marginTop":"20px"}}
        >
          Resend Project
        </button> */}
      </div>

      {ctx.postPdfUrl && (
        <a
          className="ai-floating-proposal-btn"
          href={ctx.postPdfUrl}
          target="_blank"
          rel="noreferrer"
        >
          <span className="ai-floating-icon">📄</span>
          <span>Open Proposal</span>
        </a>
      )}

      {revisionOpen && (
        <div className="ai-revision-overlay">
          <div className="ai-revision-modal">
            <h3>Request AI Revision</h3>
            <p>
              Tell the AI exactly what must be corrected in the visual proposal.
            </p>

            <textarea
              value={revisionPrompt}
              onChange={(e) => setRevisionPrompt(e.target.value)}
              placeholder="Example: Keep the Le3eD logo black. Do not change it to red. Replace the existing old sign with the supplied Le3eD artwork only."
            />

            <div className="ai-revision-actions">
              <button onClick={() => setRevisionOpen(false)}>Cancel</button>
              <button onClick={handleApplyRevision}>Restart with revision</button>
            </div>
          </div>
        </div>
      )}

      {showRenderModal && (
        <div
          className="ai-render-modal"
          onClick={() => setShowRenderModal(false)}
        >

          <div
            className="ai-render-modal-card"
            onClick={(e) => e.stopPropagation()}
          >

            {!renderReady ? (
              <>
                <div className="ai-big-spinner" />
                <h2>Finalizing AI Render</h2>
                <p>
                  Applying final adjustments...
                </p>
              </>
            ) : (
              <>
                <img
                  src={aiPreviewImage}
                  alt="AI Render"
                  className="ai-render-modal-image"
                />
                <br></br>
                <button
                  className="ai-render-modal-btn"
                  onClick={() => setShowRenderModal(false)}
                >
                  Continue
                </button>
              </>
            )}

          </div>

        </div>
      )}

    </div>
  );
}
